import axiosLib, { AxiosError } from "axios";
import { MaxRetriesError } from "~/helpers/errors";
import { paths } from "~/router/paths";
import { excludePaths } from "~/constants/paths";
const apiURL: string = import.meta.env.VITE_API_URL;

// Access + refresh tokens live only in HttpOnly cookies set by the API
// (never readable by JS — see the security ticket on localStorage-stored
// auth tokens). withCredentials makes the browser attach/accept them on
// every request instead of us building an Authorization header by hand.
export const axios = axiosLib.create({
  timeout: 60000,
  withCredentials: true,
});

const getURL = (path: string) =>
  `${apiURL}/${path.endsWith("/") ? path : path + "/"}`;

// The one in-flight refresh. Every caller that hits a 401 awaits this same
// promise, so a page-load batch of N requests produces one refresh call
// rather than N.
let inFlightRefresh: Promise<boolean> | null = null;

const requestNewAccessToken = async (): Promise<boolean> => {
  try {
    // A bare axios call on purpose. Going through sendPost would re-enter
    // handleErrorWrapper, which retries and signs out on its own — making the
    // outcome of a refresh impossible to reason about from here, and nesting
    // one retry budget inside another. No body: the refresh token travels as
    // an HttpOnly cookie, and the API reissues the access cookie on success.
    await axios.post(getURL("auth/token/refresh/"));
    return true;
  } catch {
    // Includes the 401 that means the refresh token is itself dead/absent.
    return false;
  }
};

/**
 * Refreshes the access-token cookie, coalescing concurrent callers into one
 * request. Resolves false when the refresh was refused, which is not
 * retryable: the caller's job is then to sign the user out, not to try again.
 */
export const ensureFreshToken = (): Promise<boolean> => {
  if (!inFlightRefresh) {
    inFlightRefresh = requestNewAccessToken().finally(() => {
      inFlightRefresh = null;
    });
  }

  return inFlightRefresh;
};

export const forceSignOut = () => {
  if (
    excludePaths.find((path) => new RegExp(path).test(window.location.pathname))
  ) {
    return; // Prevent sign out on pages where the user is not logged in.
  }

  // The cookies are HttpOnly — only the server can clear them. Best-effort:
  // the user is being sent to /login regardless, so a failed request here
  // just leaves stale cookies that the next authenticated call will 401 on
  // anyway.
  axios.post(getURL("auth/logout/")).catch(() => {});

  window.location.href = paths.auth.login;
};

const handleErrorWrapper = async <T = any>(
  next: Function,
  retries: number = 5
): Promise<T | void> => {
  if (retries <= 0) {
    throw new MaxRetriesError("Max retries reached");
  }

  try {
    return await next();
  } catch (err: unknown) {
    if (err instanceof AxiosError) {
      if (err.response) {
        const { status } = err.response;

        if (status === 402) {
          localStorage.setItem("plansModal", "true");
        }

        if (status === 401) {
          const url = err?.config?.url ?? "";

          // A 401 from the refresh endpoint itself is terminal — the refresh
          // token is dead/absent, so retrying only produces more 401s.
          if (url.includes("/token/refresh/")) {
            return forceSignOut();
          }

          // auth/user/ is how the app silently checks "am I logged in" (e.g.
          // on load). A 401 there might mean a stale-but-refreshable access
          // token (worth one retry) or a genuinely anonymous visitor with no
          // cookies at all (retry also 401s) — either way it's not a signed-
          // in session going bad, so a failed retry is just "not logged in,"
          // not a forced sign-out/redirect.
          if (url.includes("/auth/user/")) {
            const refreshed = await ensureFreshToken();
            if (!refreshed) return Promise.reject(err);

            return handleErrorWrapper(next, retries - 1);
          }

          // Other endpoints under /auth/ (login, register, ...) 401ing means
          // bad credentials, not an expired session — let the caller handle
          // it without attempting a refresh or forcing a sign-out redirect.
          if (url.includes("/auth/")) {
            return Promise.reject(err);
          }

          // The access-token cookie can't be inspected client-side, so every
          // other 401 is treated as "might be an expired access token": try
          // one refresh (coalesced across concurrent callers) and retry.
          const refreshed = await ensureFreshToken();
          if (!refreshed) return forceSignOut();

          return handleErrorWrapper(next, retries - 1);
        }
      }
    }
    return Promise.reject(err);
  }
};

export const sendGet = async (path: string, params?: Object): Promise<any> => {
  return handleErrorWrapper(async () => {
    return axios.get(getURL(path), { params });
  });
};

export const sendPost = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.post(getURL(path), params);
  });
};

export const sendPut = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.put(getURL(path), params);
  });
};

export const sendDelete = async (path: string, params: Object) => {
  return handleErrorWrapper(async () => {
    return axios.delete(getURL(path), params);
  });
};
